import { errorWithNothingCachedToShow } from '../../lib/query-error';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { Goal, GoalActivity } from '@localagent/sdk';
import { Badge } from '@localagent/ui/components/badge';
import type { OrgPosition, OrgRole } from '../../types/orgchart';
import { ROLE_DISPLAY_NAMES } from '../../types/orgchart';
import { getSDK } from '../../services/sdk-instance';
import { holdsIdentityAtom } from '../../stores/auth/atoms';
import { sdkConfiguredAtom } from '../../stores/sdk/atoms';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

const ROLE_COLORS: Record<OrgRole, 'default' | 'info' | 'success' | 'warning' | 'secondary'> = {
  ceo: 'default',
  vp: 'info',
  director: 'success',
  manager: 'warning',
  ic: 'secondary',
};

type AssignmentKind = 'initiative' | 'project' | 'task';

interface Assignment {
  key: string;
  kind: AssignmentKind;
  label: string;
  state: string;
  goalObjective: string;
  sessionId: string | null;
}

const KIND_LABELS: Record<AssignmentKind, string> = {
  initiative: 'Initiative',
  project: 'Project',
  task: 'Task',
};

function humanizeState(state: string): string {
  return state.replace(/_/g, ' ');
}

function collectAssignments(position: OrgPosition, activity: GoalActivity): Assignment[] {
  const objective = activity.goal.objective;
  const assignments: Assignment[] = [];

  for (const initiative of activity.initiatives) {
    if (initiative.owner_position_id === position.id) {
      assignments.push({
        key: `initiative-${initiative.id}`,
        kind: 'initiative',
        label: initiative.name,
        state: initiative.state,
        goalObjective: objective,
        sessionId: null,
      });
    }

    for (const project of initiative.projects) {
      if (project.assignee_position_id === position.id) {
        assignments.push({
          key: `project-${project.id}`,
          kind: 'project',
          label: project.name,
          state: project.state,
          goalObjective: objective,
          sessionId: project.session_id ?? null,
        });
      }

      for (const task of project.tasks) {
        const byPersona = Boolean(position.persona_id) && task.assignee_persona_id === position.persona_id;
        const byUser = Boolean(position.user_id) && task.assignee_user_id === position.user_id;
        if (!byPersona && !byUser) continue;
        assignments.push({
          key: `task-${task.id}`,
          kind: 'task',
          label: `${task.identifier} ${task.title}`,
          state: task.state,
          goalObjective: objective,
          sessionId: task.session_id ?? null,
        });
      }
    }
  }

  return assignments;
}

/**
 * Read-only detail for the selected position. Every edit affordance it used to
 * carry — add report, clear occupant, delete — is removed: the org chart is
 * changed by asking the docked agent (app-shell ADR-013).
 */
interface PositionDetailProps {
  position: OrgPosition;
  childCount: number;
  onClose: () => void;
}

export function PositionDetail({ position, childCount, onClose }: PositionDetailProps) {
  const navigate = useNavigate();

  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const readsEnabled = Boolean(isAuthenticated && sdkConfigured);

  const goalsQuery = useQuery<Goal[], Error>({
    queryKey: ['work', 'goals', 'all'],
    queryFn: () => getSDK().workManagement.listGoals(),
    enabled: readsEnabled,
  });
  const goals = goalsQuery.data ?? [];

  const activityQueries = useQueries({
    queries: goals.map((goal) => ({
      queryKey: ['work', 'goal-activity', goal.id],
      queryFn: (): Promise<GoalActivity> => getSDK().workManagement.getGoalActivity(goal.id),
      enabled: readsEnabled,
    })),
  });

  const assignments: Assignment[] = [];
  for (const query of activityQueries) {
    if (!query.data) continue;
    assignments.push(...collectAssignments(position, query.data));
  }

  const managedGoalSessions =
    position.role === 'manager' && position.team_id
      ? goals.flatMap((goal) =>
          goal.team_id === position.team_id && goal.manager_session_id
            ? [{
                id: goal.id,
                objective: goal.objective,
                state: goal.state,
                sessionId: goal.manager_session_id,
              }]
            : [],
        )
      : [];

  const activityError =
    errorWithNothingCachedToShow(goalsQuery.data, goalsQuery.error) ??
    activityQueries.find((query) => errorWithNothingCachedToShow(query.data, query.error))?.error ??
    null;
  const activityLoading =
    goalsQuery.isLoading || activityQueries.some((query) => query.isLoading);

  const openSession = (sessionId: string) => {
    void navigate(`/chat/${sessionId}`);
  };

  return (
    <div className="flex h-full flex-col border-l bg-background">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="text-sm font-semibold">Position Details</h3>
        <button
          onClick={onClose}
          className="rounded-sm p-1 hover:bg-accent"
          aria-label="Close details"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <h2 className="text-lg font-semibold">{position.title}</h2>
          <Badge variant={ROLE_COLORS[position.role]} className="mt-1">
            {ROLE_DISPLAY_NAMES[position.role]}
          </Badge>
        </div>

        <div className="space-y-3 text-sm">
          <div>
            <span className="text-muted-foreground">Occupant</span>
            <div className="mt-0.5 flex items-center gap-2">
              {position.user_id && (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500">
                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <span>Human User</span>
                </>
              )}
              {position.persona_id && (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-500">
                    <rect width="18" height="10" x="3" y="11" rx="2" />
                    <circle cx="12" cy="5" r="2" />
                    <path d="M12 7v4" />
                    <line x1="8" x2="8" y1="16" y2="16" />
                    <line x1="16" x2="16" y1="16" y2="16" />
                  </svg>
                  <span>AI Agent</span>
                </>
              )}
              {!position.user_id && !position.persona_id && (
                <span className="text-muted-foreground italic">Vacant</span>
              )}
            </div>
          </div>

          <div>
            <span className="text-muted-foreground">Direct Reports</span>
            <div className="mt-0.5">{childCount} position{childCount !== 1 ? 's' : ''}</div>
          </div>

          {position.created_at && (
            <div>
              <span className="text-muted-foreground">Created</span>
              <div className="mt-0.5">{new Date(position.created_at).toLocaleDateString()}</div>
            </div>
          )}
        </div>

        <div className="space-y-2 pt-4">
          <span className="text-sm text-muted-foreground">Working On</span>

          {activityError && (
            <div className="text-sm text-destructive">
              Failed to load work: {activityError.message}
            </div>
          )}

          {!activityError && activityLoading && assignments.length === 0 && (
            <LoadingState label="Loading work..." />
          )}

          {!activityError && !activityLoading && assignments.length === 0 && (
            <div className="text-sm text-muted-foreground italic">Nothing assigned</div>
          )}

          {assignments.map(({ key, kind, label, state, goalObjective, sessionId }) => (
            <div
              key={key}
              data-testid="position-work-item"
              className="rounded px-2 py-1.5 text-sm"
            >
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-[10px]">
                  {KIND_LABELS[kind]}
                </Badge>
                <span className="truncate font-medium">{label}</span>
              </div>
              <div className="mt-1 flex items-center justify-between gap-2">
                <span className="text-xs capitalize text-muted-foreground">
                  {humanizeState(state)}
                </span>
                {sessionId && (
                  <button
                    onClick={() => openSession(sessionId)}
                    data-testid="position-work-item-session"
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Open session
                  </button>
                )}
              </div>
              <div className="mt-1 truncate text-xs text-muted-foreground">
                {goalObjective}
              </div>
            </div>
          ))}
        </div>

        {managedGoalSessions.length > 0 && (
          <div className="space-y-2 pt-4">
            <span className="text-sm text-muted-foreground">Goal Sessions</span>
            {managedGoalSessions.map(({ id, objective, state, sessionId }) => (
              <div
                key={id}
                data-testid="position-goal-session"
                className="rounded px-2 py-1.5 text-sm"
              >
                <div className="truncate font-medium">{objective}</div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <span className="text-xs capitalize text-muted-foreground">
                    {humanizeState(state)}
                  </span>
                  <button
                    onClick={() => openSession(sessionId)}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Open goal session
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
